import { supabase } from './supabase';
import { v4 as uuidv4 } from 'uuid';

/**
 * Generate a unique shareable link for a deal
 * Creates or retrieves existing share_id for a deal
 */
export const generateShareLink = async (dealId) => {
  try {
    // First check if deal already has a share_id
    const { data: existingDeal, error: fetchError } = await supabase
      .from('deals')
      .select('share_id')
      .eq('id', dealId)
      .single();

    if (fetchError) throw fetchError;

    // If share_id already exists, return it
    if (existingDeal?.share_id) {
      return {
        shareId: existingDeal.share_id,
        shareUrl: `${window.location.origin}/share/${existingDeal.share_id}`,
        isNew: false
      };
    }

    // Generate new share_id
    const shareId = uuidv4();

    // Update the deal with the new share_id
    const { error: updateError } = await supabase
      .from('deals')
      .update({ share_id: shareId })
      .eq('id', dealId);

    if (updateError) throw updateError;

    return {
      shareId: shareId,
      shareUrl: `${window.location.origin}/share/${shareId}`,
      isNew: true
    };
  } catch (error) {
    console.error('Error generating share link:', error);
    throw new Error('Failed to generate share link');
  }
};

/**
 * Get deal data from a share_id (public view)
 * This will be used for the public /share/[id] route
 * Returns only the CHECKED items from the agent's draft snapshot
 */
export const getShareLinkData = async (shareId) => {
  try {
    // Fetch deal by share_id (including all fields)
    const { data: deal, error: dealError } = await supabase
      .from('deals')
      .select('*')
      .eq('share_id', shareId)
      .single();

    if (dealError) throw dealError;
    if (!deal) throw new Error('Share link not found');

    // Fetch the LATEST draft snapshot for this deal (contains CHECKED items only)
    const { data: snapshot, error: snapshotError } = await supabase
      .from('draft_snapshots')
      .select('selected_data')
      .eq('deal_id', deal.id)
      .order('created_at', { ascending: false })
      .limit(1)
      .single();

    // If no snapshot exists, fetch all milestones (fallback)
    let milestones = [];
    let deposits = [];
    let additionalTerms = [];

    if (snapshot && snapshot.selected_data) {
      // Use the checked items from draft snapshot
      milestones = snapshot.selected_data.milestones || [];
      deposits = snapshot.selected_data.deposits || [];
      additionalTerms = snapshot.selected_data.additionalTerms || [];
    } else {
      // Fallback: fetch all active milestones if no snapshot
      const { data: allMilestones, error: milestonesError } = await supabase
        .from('milestones')
        .select('*')
        .eq('deal_id', deal.id)
        .eq('status', 'active')
        .order('start_dt', { ascending: true });

      if (milestonesError) throw milestonesError;
      milestones = allMilestones || [];
    }

    // Extract additional parties from the snapshot
    let parties = [];
    if (snapshot?.selected_data?.parties && Array.isArray(snapshot.selected_data.parties)) {
      parties = snapshot.selected_data.parties;
      console.log('🔥 ShareLink - Extracted parties from snapshot:', parties);
    }

    // Try to load the original uploaded contract file
    let uploadedContract = null;
    try {
      const contracts = JSON.parse(localStorage.getItem('contractParser_offlineContracts') || '[]');
      const matchingContract = contracts.find(c => c.property_address?.includes(deal.property_street));

      if (matchingContract?.contract_filename) {
        const storageKey = `contract_file_${matchingContract.contract_filename}`;
        const fileData = localStorage.getItem(storageKey);

        if (fileData) {
          uploadedContract = {
            id: `uploaded-contract-share`,
            name: matchingContract.contract_filename,
            size: matchingContract.file_size || 0,
            type: 'application/pdf',
            uploadedAt: matchingContract.created_at || new Date().toISOString(),
            url: fileData,
            isOriginalContract: true
          };
        }
      }
    } catch (e) {
      console.warn('Error loading contract file for share link:', e);
    }

    return {
      deal,
      milestones: milestones || [],
      deposits: deposits || [],
      additionalTerms: additionalTerms || [],
      partyInfo: snapshot?.selected_data?.partyInfo || {},
      parties: parties,
      uploadedContract: uploadedContract
    };
  } catch (error) {
    console.error('Error fetching share link data:', error);
    throw new Error('Failed to load shared data');
  }
};

/**
 * Copy share link to clipboard
 */
export const copyShareLinkToClipboard = async (shareUrl) => {
  try {
    await navigator.clipboard.writeText(shareUrl);
    return true;
  } catch (error) {
    console.error('Error copying to clipboard:', error);
    return false;
  }
};

/**
 * Revoke a share link by removing share_id
 */
export const revokeShareLink = async (dealId) => {
  try {
    const { error } = await supabase
      .from('deals')
      .update({ share_id: null })
      .eq('id', dealId);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error revoking share link:', error);
    throw new Error('Failed to revoke share link');
  }
};
